import React, { useEffect } from 'react'
import Analytics from 'utils/googleAnalytics'
import { useRouter } from 'next/router'

import { store } from 'redux/store'

import { SET_ACTIVE_STATE } from 'redux/active/actions/types'
import { generateIsLarge, generateIsMidsized, generateIsMobile } from 'redux/active/initialState'
import { setAffiliateDeckLoaded } from 'services/caching.service'

import GlobalToolbar from 'components/misc/GlobalToolbar'
import Headers from 'components/misc/Headers'
import Footer from 'components/misc/Footer'
import ToastContainer from 'components/toasts/ToastContainer'
import SearchV2 from 'components/cardSearchPanel/searchV2'
import ScrollZone from 'components/deckPage/dragAndDrop/ScrollZone'
import VideoAd from 'components/advertisments/InhouseVideoAd'
import { CardModal } from 'components/card/CardModal'
import GenericGlobalCardModal from 'components/card/GenericGlobalCardModal'
import PinnedCardsOverlay from 'components/pinnedCards/pinnedCardsOverlay'
import NavigationDrawer, { useNavdrawerSwipe } from 'components/misc/GlobalToolbar/NavigrationDrawer'

import { DEFAULT_DESCRIPTION } from 'utils/DefaultDescription'

import styles from './pageWrapper.module.scss'
import InhouseFixedAd from 'components/advertisments/InhouseFixedAd'

type Props = {
  title?: string | null
  description?: string
  robots?: string
  rightContent?: any
  noFooter?: boolean
  lockable?: boolean
  children?: React.ReactNode
  dndPage?: boolean
  noSearch?: boolean
  className?: string
  noScroll?: boolean
  noGenericMobileNav?: boolean
}

/**
 * This component is called within all page components. It renders the global toolbar, the sidebars, the body,
 * the footer, the metadata with React-Helmet and listens to the page width to detect mobile.
 */
const PageWrapper = ({
  title,
  description,
  robots,
  noFooter,
  children,
  lockable,
  className = '',
  noSearch,
  dndPage,
}: Props) => {
  const router = useRouter()
  const swipeRef = useNavdrawerSwipe()

  const updateDimensions = () => {
    const isMobile = generateIsMobile()
    const isMidsize = generateIsMidsized()
    const isLarge = generateIsLarge()

    store.dispatch({ type: SET_ACTIVE_STATE, payload: { isMobile, isMidsize, isLarge } })
  }

  useEffect(() => {
    const referredBy = router.query.referrer || null

    if (referredBy) {
      Analytics.signalEvent(`ReferredBy: ${referredBy}`)

      // Sets cookie used at registration time to track who referred the user
      setAffiliateDeckLoaded(`${referredBy}`)
    }

    updateDimensions()

    window.addEventListener('resize', updateDimensions)

    return () => window.removeEventListener('resize', updateDimensions)
  }, [])

  const pageStyles: React.CSSProperties = {}

  if (lockable) pageStyles.display = 'flex'

  return (
    <>
      <GlobalToolbar noSearch={noSearch} />

      <div ref={swipeRef} style={pageStyles}>
        <SearchV2 lockable={lockable} />
        <PinnedCardsOverlay />
        <NavigationDrawer />

        <div className={styles.content}>
          {dndPage && <ScrollZone />}
          <main className={`${styles.main} ${className}`}>{children}</main>

          {!noFooter && <Footer />}
          {dndPage && <ScrollZone bottom />}
        </div>
      </div>

      {/* {!noGenericMobileNav && <GenericFloatingNavbar />} */}

      {/* Globally loded modals. We should probably move at least 1 or two of these down a level (or to where they're actually invoked) */}
      <CardModal />
      <GenericGlobalCardModal />
      <ToastContainer />

      <InhouseFixedAd />
      <VideoAd />

      {/* Intentionally putting headers at the bottom of the <head> */}
      {/* The way NEXT renders headers is by order in which them appear. By pushing the defaults to the bottom, any ad-hoc headers declared in the children of the page wrapper will be the primaries */}
      <Headers description={description ? description : DEFAULT_DESCRIPTION} title={title} robots={robots} />
    </>
  )
}

export default PageWrapper
